// Sample data for SystemRecommendation component
// This demonstrates the expected data structure for the component

import { RecommendationData } from './types';

export const sampleRecommendationData: RecommendationData = {
  systemType: 'system-boiler',
  systemName: 'System Boiler with Unvented Cylinder',
  confidence: {
    percentage: 85,
    level: 'high',
    breakdown: [
      {
        factor: 'Heat Loss Calculation',
        impact: 25,
        description: 'Calculated from property characteristics',
      },
      {
        factor: 'Occupancy Match',
        impact: 20,
        description: '4-person household well suited',
      },
      {
        factor: 'Space Availability',
        impact: 15,
        description: 'Adequate space for cylinder confirmed',
      },
      {
        factor: 'Current System Age',
        impact: 10,
        description: 'Similar system already in place',
      },
      {
        factor: 'Gas Supply',
        impact: 15,
        description: 'Gas available, not verified',
      },
    ],
  },
  heatLossKw: 12.5,
  heatLossSource: 'calculated',
  flowTemp: 65,
  occupancy: 4,
  recommendedOutput: 15,
  specificModel: 'Worcester Bosch Greenstar 4000 18kW + 250L Unvented Cylinder',
  reasons: [
    {
      category: 'fit',
      title: 'Perfect for Your 4-Person Home',
      points: [
        'Endless hot water for multiple showers running at once',
        'Works with your existing radiators',
        'Space available in airing cupboard for cylinder',
        'Gas supply already available',
      ],
      assumptions: ['cylinderSpace', 'gasSupply'],
    },
    {
      category: 'efficiency',
      title: 'Lower Bills, High Efficiency',
      points: [
        '94% efficiency rating - among the best available',
        'Smart controls included - save 10-15% on heating bills',
        'Well-insulated cylinder keeps water hot for hours',
        'Estimated £180/year saving vs your current system',
      ],
    },
    {
      category: 'installation',
      title: 'Quick 2-Day Installation',
      points: [
        'Most radiators and pipework can stay',
        'New boiler fits in existing location',
        'Minimal disruption to your daily routine',
        'No major building work required',
      ],
    },
    {
      category: 'flexibility',
      title: 'Future-Ready System',
      points: [
        'Can add solar panels later without major changes',
        'Compatible with smart home systems',
        '10-year warranty for peace of mind',
        'Easy to maintain and repair',
      ],
    },
  ],
  installationScope: {
    stays: [
      'All existing radiators',
      'Most pipework',
      'Gas supply connection',
      'Existing flue route (compatible)',
    ],
    changes: [
      {
        item: 'New System Boiler',
        description: 'Worcester Bosch Greenstar 4000 18kW',
        isNew: false, // replacement
      },
      {
        item: 'New Unvented Cylinder',
        description: '250L high-performance cylinder',
        isNew: true,
      },
      {
        item: 'Smart Controls',
        description: 'Smart thermostat with app control',
        isNew: true,
      },
      {
        item: 'Magnetic Filter',
        description: 'Protects system from sludge',
        isNew: true,
      },
    ],
    disruption: {
      totalDays: 2,
      timeline: [
        {
          day: 1,
          activities: [
            'Remove old boiler',
            'Install new boiler',
            'First fix pipework for cylinder',
          ],
        },
        {
          day: 2,
          activities: [
            'Install unvented cylinder',
            'Connect all systems',
            'Commission and test',
            'Show you how everything works',
          ],
        },
      ],
      waterOff: '4-6 hours on Day 2',
      heatingOff: '6-8 hours on Day 1',
    },
  },
  alternatives: [
    {
      systemType: 'combi',
      systemName: 'Combi Boiler',
      notRecommendedReason: 'Not ideal for your household size and hot water needs',
      points: [
        'Lower upfront cost (£500-800 less)',
        'No cylinder needed - saves space',
        '⚠️ Limited to one shower at a time',
        '⚠️ Flow rate drops with multiple taps running',
        '⚠️ Not suitable for 4-person household with high hot water demand',
      ],
      couldWorkIf: 'Only 1-2 people in the home and low hot water usage',
    },
    {
      systemType: 'heat-pump-air',
      systemName: 'Air Source Heat Pump',
      notRecommendedReason:
        'Great for the environment, but requires larger upfront investment and radiator upgrades',
      points: [
        '✓ Lowest running costs (£400-600/year savings)',
        '✓ Future-proof and environmentally friendly',
        '✓ Eligible for £7,500 government grant',
        '⚠️ Higher upfront cost even with grant (£8,000-12,000)',
        '⚠️ All radiators need upgrading to work at lower temperatures',
        '⚠️ External unit needs outdoor space',
        '⚠️ Longer payback period (8-10 years)',
      ],
      couldWorkIf:
        'Planning to stay 10+ years and willing to upgrade all radiators',
      revisitIf: 'Staying long-term or doing major renovation',
    },
  ],
  assumptions: [
    {
      type: 'heatLoss',
      description:
        'Heat loss calculated at 12.5kW based on property age, size, and typical insulation',
      impact:
        'Affects boiler size recommendation. We\'ll measure room-by-room during survey.',
      confidence: 'medium',
      needsVerification: true,
    },
    {
      type: 'cylinderSpace',
      description: 'Assumed space available in airing cupboard for 250L cylinder',
      impact:
        'If no space, may need regular boiler with vented cylinder or combi as fallback',
      confidence: 'medium',
      needsVerification: true,
    },
    {
      type: 'gasSupply',
      description: 'Gas supply present and adequate capacity',
      impact: 'If gas not available, would need to consider oil or electric alternatives',
      confidence: 'high',
      needsVerification: true,
    },
    {
      type: 'radiators',
      description:
        'Existing radiators suitable for 65°C flow temperature (standard for modern gas boilers)',
      impact:
        'Radiators likely fine for gas boiler. Heat pump would require upgrades.',
      confidence: 'high',
      needsVerification: true,
    },
    {
      type: 'pipework',
      description: 'Existing pipework in good condition, no major leaks or corrosion',
      impact:
        'May need some pipework updates during installation, will assess on survey',
      confidence: 'medium',
      needsVerification: true,
    },
  ],
  pricing: {
    rangeMin: 4500,
    rangeMax: 6000,
    includes: [
      'Worcester Bosch system boiler (18kW)',
      '250L unvented cylinder',
      'Smart controls',
      'Magnetic filter',
      'Full installation and commissioning',
      '10-year warranty on boiler',
      '2-year warranty on cylinder',
    ],
    financeAvailable: true,
    financeMonthly: 125,
    financeAPR: 9.9,
  },
  propertyContext: {
    houseType: '3-bed semi-detached',
    ageBand: '1960s',
    occupancy: 4,
    currentSystem: 'Regular boiler with vented cylinder',
    lengthOfStay: 'Planning to stay 5-10 years',
  },
  // New customer-centric sections
  houseFit: {
    confidence: {
      level: 'high',
      summary: 'System perfectly matches your property type and existing infrastructure',
    },
    physicalCompatibility: {
      propertyType: '3-bed semi-detached',
      propertySize: '120m² (approx 1,300 sq ft)',
      existingInfrastructure: [
        'Gas supply connection',
        'Suitable radiators (10 total)',
        'Existing boiler location',
        'Airing cupboard space',
      ],
    },
    flowAndPressure: {
      adequate: true,
      details: 'Mains pressure measured at 2.5 bar - ideal for unvented cylinder',
    },
    gasSupply: {
      available: true,
      capacity: '22mm gas pipe - adequate for 18kW boiler',
    },
    installationLocations: {
      boiler: 'Kitchen (existing location)',
      cylinder: 'Airing cupboard (first floor)',
      controls: 'Hallway thermostat + wireless room sensors',
    },
    buildingWork: {
      required: false,
      details: [
        'Minor pipework modifications only',
        'No structural changes needed',
      ],
    },
    disruption: {
      days: 2,
      impact: 'Hot water off for 4-6 hours on Day 2. Heating off 6-8 hours on Day 1. Full system operational by end of Day 2.',
    },
  },
  familyFit: {
    confidence: {
      level: 'high',
      summary: 'Excellent match for your household size and hot water usage patterns',
    },
    occupants: {
      count: 4,
      bedrooms: 3,
      bathrooms: 2,
    },
    hotWaterDemand: {
      showersPerDay: 4,
      bathsPerWeek: 3,
      simultaneousUse: 'Can run 2 showers simultaneously without pressure drop',
    },
    lifestyleConsiderations: [
      'Family with teenagers - high morning hot water demand',
      'Two bathrooms need simultaneous hot water capability',
      'Occasional visitors/guests requiring flexibility',
      'Active lifestyle with frequent showering',
    ],
    usage: {
      peak: '7-9am weekday mornings, 6-8pm evenings',
      typical: 'Morning showers for 4 people, evening cooking and cleaning, weekend baths',
    },
  },
  futureFit: {
    confidence: {
      level: 'high',
      summary: 'Long lifespan with excellent smart home integration and upgrade options',
    },
    lifespan: {
      expected: '15-20 years with proper maintenance',
      warranty: '10-year warranty on boiler, 2-year warranty on cylinder',
    },
    smartHome: {
      compatible: true,
      features: [
        'Smart thermostat included',
        'Smartphone app control from anywhere',
        'Integration with Alexa, Google Home',
        'Room-by-room temperature control (optional)',
        'Usage monitoring and optimization',
      ],
    },
    upgradePath: {
      futureProof: true,
      options: [
        'Add solar thermal panels later (cylinder is solar-ready)',
        'Upgrade to multi-zone heating controls',
        'Add weather compensation for efficiency',
        'Integration with home battery storage',
      ],
    },
    environmental: {
      carbonReduction: '1.2 tonnes CO2 per year vs current system',
      efficiency: '94% ErP A-rated - among the best available',
    },
    regulations: {
      compliant: true,
      futureProof: 'Meets all current Building Regulations. Gas boilers remain compliant until at least 2035.',
    },
  },
  financialFit: {
    confidence: {
      level: 'high',
      summary: 'Clear payback period with significant annual savings and finance options available',
    },
    upfrontCosts: {
      equipment: 3200,
      installation: 1800,
      additional: [
        { item: 'Magnetic filter', cost: 150 },
        { item: 'Smart controls', cost: 250 },
        { item: 'System cleanse & treat', cost: 100 },
      ],
      total: 5500,
    },
    runningCosts: {
      currentAnnual: 1200,
      newAnnual: 850,
      monthlySaving: 29,
    },
    payback: {
      years: 15.8,
      breakEvenYear: 16,
    },
    financing: {
      available: true,
      monthlyPayment: 125,
      apr: 9.9,
      term: 60,
    },
    incentives: {
      available: [
        'ECO4 scheme may cover partial costs (eligibility check required)',
        'Energy Company Obligation - up to £1,500 for eligible households',
      ],
      totalValue: 1500,
    },
  },
};
